import { useEffect, useState } from "react";
import { MyBadges } from "@/components/Badges";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StreakFlame } from "@/components/StreakFlame";
import { Button } from "@/components/ui/button";
import { MessagesSquare, Sparkles, LayoutDashboard, Settings as Cog, BookMarked, Heart, LayoutGrid, CalendarDays, Newspaper, Bell, PlayCircle } from "lucide-react";

export const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [digestCount, setDigestCount] = useState(0);

  // How many stories are waiting in today's digest.
  useEffect(() => {
    if (!user) return;
    api.get("/news/digest/badge")
      .then(({ data }) => setDigestCount(data.count || 0))
      .catch((e) => console.error("could not check the digest", e));
  }, [user, pathname]);

  const links = user
    ? [
        { to: "/news", label: "News", icon: Newspaper, id: "news" },
        { to: "/messages", label: t("transmessages"), icon: MessagesSquare, id: "messages" },
        { to: "/chat", label: t("chat"), icon: Sparkles, id: "chat" },
        { to: "/phrasebook", label: "Smart Tools", icon: BookMarked, id: "phrasebook" },
        { to: "/prompts", label: "Prompts", icon: LayoutGrid, id: "prompts" },
        { to: "/pricing", label: t("pricing"), icon: null, id: "pricing" },
        { to: "/support", label: "Support", icon: Heart, id: "support" },
        { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
        { to: "/settings", label: t("settings"), icon: Cog, id: "settings" },
        ...(user.role === "admin"
          ? [{ to: "/admin", label: t("admin"), icon: LayoutDashboard, id: "admin" }]
          : []),
      ]
    : [
        { to: "/tour", label: "Tour", icon: PlayCircle, id: "tour" },
        { to: "/pricing", label: t("pricing"), icon: null, id: "pricing" },
        { to: "/support", label: "Support", icon: Heart, id: "support" },
      ];

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B0C0F]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-5 py-3 sm:px-8">
        <Link to="/" data-testid="brand-logo" className="group flex items-center">
          <img
            src="/logo.png"
            alt="ChatMiLu"
            data-testid="brand-logo-image"
            className="h-11 w-auto drop-shadow-[0_0_0_rgba(26,125,255,0)] transition-[filter,transform] duration-300 group-hover:scale-[1.03] group-hover:drop-shadow-[0_0_14px_rgba(26,125,255,0.45)] sm:h-12"
          />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={`relative rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                pathname === l.to ? "bg-surfacealt text-ink" : "text-ink-soft hover:text-brand"
              }`}
            >
              {l.label}
              {l.id === "news" && digestCount > 0 && (
                <span data-testid="nav-news-bell"
                      title={`${digestCount} stories in today's digest`}
                      className="absolute -end-1 -top-1 inline-flex items-center gap-0.5 rounded-full bg-brand px-1.5 text-[10px] font-bold text-white">
                  <Bell className="h-2.5 w-2.5" />{digestCount}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <LanguageSwitcher compact />
          {user ? (
            <>
              <StreakFlame />
              <span
                data-testid="header-user-email"
                className="hidden max-w-[12rem] items-center gap-1.5 truncate text-sm text-ink-soft sm:flex"
              >
                {user.name || user.email}
                <MyBadges />
              </span>
              <Button
                data-testid="logout-button"
                variant="outline"
                className="min-h-[40px] rounded-full active:scale-95"
                onClick={async () => {
                  await logout();
                  nav("/");
                }}
              >
                {t("logout")}
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="login-button"
                variant="ghost"
                className="min-h-[40px] rounded-full active:scale-95"
                onClick={() => nav("/login")}
              >
                {t("login")}
              </Button>
              <Button
                data-testid="signup-button"
                className="min-h-[40px] rounded-full bg-brand hover:bg-brand-hover active:scale-95"
                onClick={() => nav("/signup")}
              >
                {t("signup")}
              </Button>
            </>
          )}
        </div>
      </div>
      <nav className="flex gap-1 overflow-x-auto border-t border-white/10 px-5 py-2 md:hidden cml-scroll">
        {links.map((l) => (
          <Link
            key={l.id}
            to={l.to}
            data-testid={`nav-mobile-${l.id}`}
            className="whitespace-nowrap rounded-full px-3 py-1.5 text-sm text-ink-soft"
          >
            {l.label}
          </Link>
        ))}
      </nav>
    </header>
  );
};
